import { useEffect, useState } from 'react';
import { FormHelperText, Grid, InputAdornment, InputLabel, OutlinedInput } from '@mui/material';
import HandshakeIcon from '@mui/icons-material/Handshake';
import { FormDrawer, FormDrawerGroup } from '../../../../components/form-drawer';
import { RichTextEditor } from '../../components/rich-text-editor';
import { resolveDateValue, toDateInputValue } from '../../../../utils/date';
import { formatCurrency, parseNumberInput } from '../../../../utils/format';
import { IAgreementAddEditRequest } from '../../../../model/apm/agreement/request/agreement-add-edit-request';
import { IAgreementResponse } from '../../../../model/apm/agreement/response/agreement-response';

interface AgreementDrawerProps {
    open: boolean;
    initial?: IAgreementResponse;
    isSaving: boolean;
    onClose: () => void;
    onSave: (value: IAgreementAddEditRequest) => void;
}

export function AgreementDrawer({ open, initial, isSaving, onClose, onSave }: AgreementDrawerProps) {
    const [form, setForm] = useState<IAgreementAddEditRequest>({});
    const [showValidationErrors, setShowValidationErrors] = useState(false);
    const isEdit = !!initial;
    const periodInvalid = !!form.startDate && !!form.endDate && form.endDate < form.startDate;
    const amountInvalid = (form.transferAmount ?? 0) < 0 || (form.counterpartAmount ?? 0) < 0;
    const errors = {
        superiorBody: showValidationErrors && !form.superiorBody?.trim(),
        startDate: showValidationErrors && !form.startDate,
        endDate: showValidationErrors && periodInvalid,
        amount: showValidationErrors && amountInvalid,
    };
    const total = (form.transferAmount ?? 0) + (form.counterpartAmount ?? 0);

    useEffect(() => {
        if (!open) return;
        setForm(initial
            ? {
                superiorBody: initial.superiorBody,
                subordinateBody: initial.subordinateBody,
                managementUnit: initial.managementUnit,
                startDate: toDateInputValue(initial.startDate),
                endDate: toDateInputValue(initial.endDate),
                objective: initial.objective,
                transferAmount: initial.transferAmount,
                counterpartAmount: initial.counterpartAmount,
            }
            : {});
        setShowValidationErrors(false);
    }, [open, initial]);

    function update(patch: Partial<IAgreementAddEditRequest>) {
        setForm((current) => ({ ...current, ...patch }));
    }

    function handleSave() {
        if (!form.superiorBody?.trim() || !form.startDate || periodInvalid || amountInvalid) {
            setShowValidationErrors(true);
            return;
        }
        onSave({
            ...form,
            superiorBody: form.superiorBody.trim(),
            subordinateBody: form.subordinateBody?.trim() || undefined,
            managementUnit: form.managementUnit?.trim() || undefined,
            startDate: resolveDateValue(form.startDate, initial?.startDate),
            endDate: resolveDateValue(form.endDate, initial?.endDate),
            objective: form.objective || undefined,
        });
    }

    return (
        <FormDrawer
            open={open}
            icon={<HandshakeIcon />}
            title={isEdit ? 'Editar convênio' : 'Novo convênio'}
            subtitle={isEdit ? initial?.superiorBody : 'Convênio ou repasse recebido pela entidade'}
            saveLabel={isEdit ? 'Salvar alterações' : 'Adicionar convênio'}
            isSaving={isSaving}
            onClose={onClose}
            onSave={handleSave}
        >
            <FormDrawerGroup title="Identificação" description="Órgãos concedentes e unidade responsável pela gestão do recurso.">
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <InputLabel required>Órgão superior</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoFocus
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.superiorBody || ''}
                            onChange={(e) => update({ superiorBody: e.target.value })}
                            error={errors.superiorBody}
                        />
                        {errors.superiorBody && <FormHelperText error>Órgão superior é obrigatório</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Órgão subordinado</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.subordinateBody || ''}
                            onChange={(e) => update({ subordinateBody: e.target.value })}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Unidade gestora</InputLabel>
                        <OutlinedInput
                            fullWidth
                            autoComplete="off"
                            inputProps={{ maxLength: 250 }}
                            value={form.managementUnit || ''}
                            onChange={(e) => update({ managementUnit: e.target.value })}
                        />
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Vigência e valores">
                <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                        <InputLabel required>Início</InputLabel>
                        <OutlinedInput fullWidth type="date" value={form.startDate || ''} onChange={(e) => update({ startDate: e.target.value || undefined })} error={errors.startDate} />
                        {errors.startDate && <FormHelperText error>Data de início é obrigatória</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Término</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="date"
                            inputProps={{ min: form.startDate }}
                            value={form.endDate || ''}
                            onChange={(e) => update({ endDate: e.target.value || undefined })}
                            error={errors.endDate}
                        />
                        {errors.endDate && <FormHelperText error>O término deve ser igual ou posterior ao início</FormHelperText>}
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Valor do repasse</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            value={form.transferAmount ?? ''}
                            onChange={(e) => update({ transferAmount: parseNumberInput(e.target.value) })}
                            error={errors.amount && (form.transferAmount ?? 0) < 0}
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <InputLabel>Valor da contrapartida</InputLabel>
                        <OutlinedInput
                            fullWidth
                            type="number"
                            inputProps={{ min: 0, step: 0.01 }}
                            startAdornment={<InputAdornment position="start">R$</InputAdornment>}
                            value={form.counterpartAmount ?? ''}
                            onChange={(e) => update({ counterpartAmount: parseNumberInput(e.target.value) })}
                            error={errors.amount && (form.counterpartAmount ?? 0) < 0}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        {errors.amount
                            ? <FormHelperText error>Os valores não podem ser negativos</FormHelperText>
                            : <FormHelperText>Valor total do convênio: {formatCurrency(total)}</FormHelperText>}
                    </Grid>
                </Grid>
            </FormDrawerGroup>

            <FormDrawerGroup title="Objetivo">
                <RichTextEditor value={form.objective} onChange={(objective) => update({ objective })} minHeight={160} />
            </FormDrawerGroup>
        </FormDrawer>
    );
}
